/**
 * ========================================
 * CUSTOM ASSET - eye-selector-modded.js
 * Version: 1.2
 * Created: 2026-10-02
 * Last Modified: 2026-10-02
 * Client: Studio Optix
 * ========================================
 *
 * PURPOSE:
 * <eye-selector-modded> custom element for the contacts per-eye quantity
 * block (eye-selector-modded.liquid):
 * - Keeps the product form's main quantity equal to right + left (live on
 *   input, re-asserted on form submit).
 * - Derives the "Eye" line item property (Right / Left / Both) from the
 *   two counts into a hidden input.
 * - Blocks add to cart while both counts are 0 via setCustomValidity, so
 *   native form validation shows the message and no empty line is added.
 *
 * MODIFICATIONS:
 * - v1.2 (2026-10-02): Fix quantity stuck at 1 when the template has no
 *   stock quantity block - the form then has NO quantity input, so the
 *   sync had nothing to update and Shopify defaulted to 1. The element
 *   now injects a hidden quantity input into the form when none exists.
 * - v1.1 (2026-10-02): Simplified - two always-visible quantity fields,
 *   Eye derived automatically; radio/split logic removed.
 * - v1.0 (2026-10-02): Initial creation.
 *
 * DEPENDENCIES:
 * - The buy buttons form (id passed via data-form-id). Quantity input is
 *   looked up through form.elements, so it is found whether it lives
 *   inside the form or is associated via the form attribute.
 */

class EyeSelectorModded extends HTMLElement {
  #controller = new AbortController();

  get #form() {
    const id = this.dataset.formId;
    const form = id ? document.getElementById(id) : null;

    return form instanceof HTMLFormElement ? form : null;
  }

  get #quantityInput() {
    const named = this.#form?.elements.namedItem('quantity');

    if (named instanceof HTMLInputElement) return named;
    if (named instanceof RadioNodeList && named[0] instanceof HTMLInputElement) return named[0];

    return null;
  }

  /**
   * The contacts template has no stock quantity block, so the form may
   * have no quantity field at all (Shopify then defaults to 1). Create a
   * hidden one in that case so the per-eye total is actually charged.
   */
  #ensureQuantityInput() {
    const existing = this.#quantityInput;
    if (existing) return existing;

    const form = this.#form;
    if (!form) return null;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'quantity';
    input.value = '1';
    form.append(input);

    return input;
  }

  get #rightQty() {
    return this.querySelector('[ref="rightQty"]');
  }

  get #leftQty() {
    return this.querySelector('[ref="leftQty"]');
  }

  get #eyeProperty() {
    return this.querySelector('[ref="eyeProperty"]');
  }

  connectedCallback() {
    const { signal } = this.#controller;

    for (const input of [this.#rightQty, this.#leftQty]) {
      input?.addEventListener('input', () => this.#sync(), { signal });
      input?.addEventListener('change', () => this.#sync(), { signal });
    }

    // Whatever the quantity ends up as elsewhere, the charged quantity
    // matches the per-eye counts at the moment of add to cart.
    this.#form?.addEventListener('submit', () => this.#sync(), { signal });

    this.#sync();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #sync() {
    const rightInput = this.#rightQty;
    const leftInput = this.#leftQty;
    const right = Math.max(0, parseInt(rightInput?.value, 10) || 0);
    const left = Math.max(0, parseInt(leftInput?.value, 10) || 0);
    const total = right + left;

    // Derived Eye property for quick scanning on the order.
    const eyeProperty = this.#eyeProperty;
    if (eyeProperty instanceof HTMLInputElement) {
      let eye = '';
      if (right > 0 && left > 0) eye = 'Both';
      else if (right > 0) eye = 'Right';
      else if (left > 0) eye = 'Left';
      eyeProperty.value = eye;
      // An empty value would still submit "Eye: " - only submit when set.
      eyeProperty.disabled = eye === '';
    }

    // Native validation blocks add to cart while both counts are 0.
    if (rightInput instanceof HTMLInputElement) {
      rightInput.setCustomValidity(total === 0 ? 'Enter boxes for at least one eye' : '');
    }

    const summary = this.querySelector('[ref="summary"]');
    if (summary) {
      summary.textContent =
        total > 0 ? `${total} ${total === 1 ? 'box' : 'boxes'} total — ${right} right / ${left} left` : '';
    }

    if (total > 0) {
      const quantityInput = this.#ensureQuantityInput();
      if (quantityInput && parseInt(quantityInput.value, 10) !== total) {
        quantityInput.value = String(total);
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
}

if (!customElements.get('eye-selector-modded')) {
  customElements.define('eye-selector-modded', EyeSelectorModded);
}
